import useCustomHook from '@/hooks/useCustomHook';
import useInterval from '@/hooks/useInterval';
import { useEffect, useRef } from 'react';
import {
  ScreenStatus,
  createScreens,
  getScreenStatusToChange,
  questionTimer,
} from '@/shared/Activities/RealTimeActivity/Players/SortImagesActivity/sortImages.utils';
import getActivityTypeConf from '@/modules/activities/ActivityTypesConf';
import { deepClone } from '@/modules/objectUtils';

export default function useConceptQuestActivity({
  activity,
  activityConf,
  taskActivityConf,
  initialTaskProgress,
  taskProgressAPI,
  onFinishedActivity,
}) {
  const folder = activity?.data?.folder;
  const questions = activity?.data?.questions;
  const lastSentProgress = useRef();

  const { state, actions } = useCustomHook({
    defaultState: {
      activityConf: null,
      screens: [],
      currentScreen: null,
      secondsLeft: 0,
      audioIndexPlaying: null,
      answers: [],
      finished: false,
    },
    actions: {
      initActivity: async () => {
        const currentScreen = {
          ...state.screens[0],
          status: ScreenStatus.audioPaused,
        };

        actions.setState({
          currentScreen,
          secondsLeft: questionTimer,
          audioIndexPlaying: null,
        });
      },
      showNextScreen: async () => {
        const nextScreen = state.screens[state.currentScreen.index + 1];

        if (nextScreen) {
          const currentScreen = nextScreen;
          currentScreen.status = ScreenStatus.audioPlaying;

          actions.setState({
            currentScreen,
            secondsLeft: questionTimer,
            audioIndexPlaying: null,
          });
        } else {
          await actions.showFinishScreen();
        }
      },
      showAnswers: async () => {
        if (!ScreenStatus.isResults(state.currentScreen.status)) {
          const newCurrentScreen = {
            ...state.currentScreen,
            status: ScreenStatus.resultsPlaying,
          };

          actions.setState({
            currentScreen: newCurrentScreen,
            secondsLeft: null,
          });
        }
      },
      showFinishScreen: async () => {
        onFinishedActivity();
        actions.setState({
          currentScreen: null,
          finished: true,
        });
      },
      togglePaused: () => {
        const newScreenStatus = getScreenStatusToChange(
          state.currentScreen.status
        );
        const newCurrentScreen = {
          ...state.currentScreen,
          status: newScreenStatus,
        };
        actions.setState({ currentScreen: newCurrentScreen });
      },
      setAudioIndexPlaying: (audioIndexPlaying) => {
        actions.setState({
          audioIndexPlaying,
        });
      },
      skipStep: () => {
        if (ScreenStatus.isAudio(state.currentScreen.status)) {
          actions.setState({
            currentScreen: {
              ...state.currentScreen,
              status: ScreenStatus.activityPlaying,
            },
          });
        } else if (ScreenStatus.isActivity(state.currentScreen.status)) {
          actions.showAnswers();
        } else {
          actions.showNextScreen();
        }
      },
      onUnansweredQuestions: () => {
        const alreadyAnsweredQuestions = state.answers.filter((answer) =>
          state.currentScreen.questions.find(
            (question) => question.id == answer.questionId
          )
        );
        actions.sharedActions.onQuestionAnswered(
          alreadyAnsweredQuestions,
          true
        );
      },
      sharedActions: {
        onQuestionAnswered: async (answers, screenFinished) => {
          const answersWithoutCurrentScreen = state.answers.filter(
            (answer) =>
              !state.currentScreen.questions.find(
                (question) => question.id == answer.questionId
              )
          );
          const allAnswers = [...answersWithoutCurrentScreen, ...answers];
          actions.setState({
            answers: allAnswers,
          });

          if (screenFinished) {
            const progressData = calculateProgress({
              screens: state.screens,
              answers: allAnswers,
              finished: state.finished,
              currentScreenIndex: state.currentScreen.index,
            });

            lastSentProgress.current = progressData.progress;
            const gamificationProgressData = {
              progress: progressData.progress,
              correctAnswers: answers
                .filter((answer) => answer.correct)
                .map((answer) => answer.id),
            };

            taskProgressAPI.PROGRESS(
              {
                ...progressData,
                state: {
                  ...state,
                },
              },
              gamificationProgressData
            );

            actions.showAnswers();
          }
        },
      },
    },
  });

  useInterval(
    () => {
      if (state.secondsLeft > 0) {
        const secondsLeft = state.secondsLeft - 1;
        actions.setState({
          secondsLeft,
        });

        if (secondsLeft == 0) {
          if (ScreenStatus.isResults(state.currentScreen.status)) {
            actions.showNextScreen();
          } else {
            actions.onUnansweredQuestions();
          }
        }
      }
    },
    state.currentScreen?.status == ScreenStatus.activityPlaying &&
      !state.finished
      ? 1000
      : null
  );

  useEffect(() => {
    if (activity) {
      let conf =
        taskActivityConf ||
        activityConf ||
        getActivityTypeConf({
          type: 'game',
          subType: 'sortImages',
          miniactivityType: 'conceptQuest',
        });

      if (
        !taskActivityConf &&
        !activityConf &&
        activity.data?.questionsPerScreen
      ) {
        conf = deepClone(conf);
        conf.options.questionsPerScreen = activity.data.questionsPerScreen;
      }
      actions.setState({
        activityConf: conf,
      });
    }
  }, [activity, activityConf, taskActivityConf]);

  useEffect(() => {
    if (initialTaskProgress?.state && !state.finished) {
      lastSentProgress.current = initialTaskProgress.progress;

      const screenAnswered =
        initialTaskProgress.state?.currentScreen?.questions?.every((question) =>
          initialTaskProgress.answers?.some(
            (answer) => answer.supposedQuestionId == question.id
          )
        );

      let currentScreen = initialTaskProgress.state?.currentScreen;
      if (screenAnswered && currentScreen) {
        currentScreen = {
          ...currentScreen,
          status: ScreenStatus.resultsPlaying,
        };
      }

      actions.setState({
        ...initialTaskProgress.state,
        currentScreen,
        answers: initialTaskProgress.answers,
      });
    } else if (
      questions?.length &&
      state.activityConf?.options?.questionsPerScreen &&
      !state.screens.length
    ) {
      const screens = createScreens({
        folder,
        questions,
        questionsPerScreen: state.activityConf?.options?.questionsPerScreen,
      });
      actions.setState({
        screens,
      });
    }
  }, [questions, state.activityConf, state.screens, initialTaskProgress]);

  useEffect(() => {
    if (state.screens.length && !state.currentScreen) {
      actions.initActivity();
    }
  }, [state.screens]);

  useEffect(() => {
    if (state.finished) {
      const progressData = calculateProgress({
        screens: state.screens,
        answers: state.answers,
        finished: state.finished,
        currentScreenIndex: state.screens.length - 1,
      });

      if (progressData.progress >= lastSentProgress.current) {
        lastSentProgress.current = progressData.progress;

        const gamificationProgressData = {
          progress: progressData.progress,
          correctAnswers: [],
        };

        taskProgressAPI.PROGRESS(
          {
            ...progressData,
            state: {
              ...state,
            },
          },
          gamificationProgressData
        );
      }
    }
  }, [state.finished]);

  return {
    state,
    actions,
  };
}

function calculateProgress({ screens, answers, finished, currentScreenIndex }) {
  let totalCount = 0;
  let correctCount = 0;
  let answeredCount = 0;
  for (const screen of screens) {
    for (const question of screen.questions) {
      totalCount++;
      const answer = answers.find((answer) => answer.questionId == question.id);
      if (answer?.correct) {
        correctCount++;
      }
    }
    if (screen.index <= currentScreenIndex) {
      answeredCount += screen.questions.length;
    }
  }
  const progress = parseInt((answeredCount * 100) / totalCount);
  const correct = parseInt((correctCount * 100) / totalCount);
  return {
    progress: progress == 100 && !finished ? 99 : progress,
    correct,
    answers,
  };
}
